// External dependencies.
import { LitElement, html, css } from "lit";
import { __ } from "@wordpress/i18n";

// Internal dependencies.
import { sharedStyles } from "./styles/shared-styles";
import { buttonStyles } from "./styles/button-styles";
import { sprintf } from 'sprintf-js';


/**
 * AppRecipeCard component class.
 *
 * @since 0.1.0
 * @class AppRecipeCard
 * @extends {LitElement}
 */
class AppRecipeCard extends LitElement {
    /**
     * Inits the component's properties.
     *
     * @since 0.1.0
     * @readonly
     * @static
     * @memberof AppRecipeCard
     */
    static get properties() {
        return {
            recipe: { type: Object },
        };
    }

    /**
     * Inits the component's CSS styles.
     *
     * @since 0.1.0
     * @readonly
     * @static
     * @memberof AppRecipeCard
     */
    static get styles() {
        return [ sharedStyles, buttonStyles, css`
            .recipe-card {
                display: flex;
                flex-wrap: wrap;
            }

            .recipe-card-col {
                flex: 0 0 100%;
            }

            .recipe-card-body {
                padding: 1rem;
                background-color: var(--color-whitesmoke);
            }

            .recipe-card-title {
                margin-bottom: 1rem;
                text-align: center;
                line-height: 1.25;
            }

            .recipe-card-share {
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 0;
                margin: 0;
                text-transform: uppercase;
                text-align: center;
                list-style: none;
            }

            .recipe-card-share__item:not(:last-child) {
                margin-right: .75rem;
            }

            .recipe-card-share__icon {
                width: 1.25rem;
                height: 1.25rem;
            }

            .recipe-card-thumbnail-link {
                display: block;
                height: 12.5rem;
            }

            @media (min-width: 576px) {
                .recipe-card {
                    height: 25.625rem;
                }

                .recipe-card-col {
                    display: flex;
                    flex: 0 0 50%;
                    align-items: stretch;
                }

                .recipe-card-col,
                .recipe-card-thumbnail-link {
                    height: inherit;
                }

                .recipe-card-body {
                    display: flex;
                    align-items: center;
                    padding-top: 2rem;
                    padding-bottom: 2rem;
                }
            }
        ` ];
    }

    /**
     * Returns the categories HTML template.
     *
     * @since 0.1.0
     * @readonly
     * @memberof AppRecipeCard
     */
    get categoriesTemplate() {
        const categoriesLength = this.recipe.categories.length;

        if ( 0 === categoriesLength ) {
            return null;
        }

        return html`
            <p class="text-center text-uppercase">
                ${this.recipe.categories.map( ( item, i ) => html`
                    <a href="${item.permalink}" class="text-reset" title="${item.name}">${item.name}</a>
                    ${i === categoriesLength - 1 ? '' : html`&sol;`}
                `)}
            </p>
        `;
    }

    /**
     * Renders the component.
     *
     * @since 0.1.0
     * @returns TemplateResult
     * @memberof AppRecipeCard
     */
    render() {
        const { title, title_full, excerpt, date, author, permalink, thumbnail } = this.recipe;

        return html`
            <article class="recipe-card">
                <div class="recipe-card-col">
                    <a href="${permalink}" title="${title_full}" class="recipe-card-thumbnail-link">
                        <slot name="recipe-thumbnail"></slot>
                    </a>
                </div>
                <div class="recipe-card-col">
                    <div class="recipe-card-body">
                        <div>
                            ${this.categoriesTemplate}
                            <h4 class="recipe-card-title">
                                <a href="${permalink}" class="text-reset" title="${title_full}">${title}</a>
                            </h4>
                            <p class="text-center small">${sprintf( __( 'Por %s', 'tio-simon-foods' ), author )} &sol; ${date}</p>
                            <p class="text-center">${excerpt}</p>
                            <a href="${permalink}" class="btn btn-ts-primary btn-block mb-3" title="${__( 'Leer Más', 'tio-simon-foods' )}">${__( 'Leer Más', 'tio-simon-foods' )}</a>
                            <ul class="recipe-card-share">
                                <li class="recipe-card-share__item">
                                    <span class="d-flex small">${__( 'Compartir receta:', 'tio-simon-foods' )}</span>
                                </li>
                                <li class="recipe-card-share__item">
                                    <a href="https://www.facebook.com/sharer/sharer.php?u=${permalink}" target="_blank" class="d-flex">
                                        <img src="${window.app.theme_file_uri}/assets/images/icon-facebook-dark.svg" class="recipe-card-share__icon">
                                    </a>
                                </li>
                                <li class="recipe-card-share__item">
                                    <a href="https://api.whatsapp.com/send?text=${permalink}" data-action="share/whatsapp/share" target="_blank" class="d-flex">
                                        <img src="${window.app.theme_file_uri}/assets/images/icon-whatsapp-dark.svg" class="recipe-card-share__icon">
                                    </a>
                                </li>
                                <li class="recipe-card-share__item">
                                    <a href="https://twitter.com/intent/tweet?url=${permalink}&text=${title}" target="_blank" class="d-flex">
                                        <img src="${window.app.theme_file_uri}/assets/images/icon-twitter-dark.svg" class="recipe-card-share__icon">
                                    </a>
                                </li>
                                <li class="recipe-card-share__item">
                                    <a href="http://pinterest.com/pin/create/button/?url=${permalink}&media=${thumbnail}&description=${title}" target="_blank" class="d-flex">
                                        <img src="${window.app.theme_file_uri}/assets/images/icon-pinterest-dark.svg" class="recipe-card-share__icon">
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </article>
        `;
    }
}

window.customElements.define( 'app-recipe-card', AppRecipeCard );
